import React, { useState, useMemo } from 'react';
import GameHeader from '../other_components/GameHeader';
import WaterUsageChart from '../graph_pages/graph_components/WaterUsageChart';
import HalfDoughnutChart from '../graph_pages/graph_components/HalfDoughnutChart';
import BreakdownBars from '../graph_pages/graph_components/BreakdownBars';
import {
  breakdownWith, chartDisplayOf, chartSeriesOf, savingsPercent, savingsWith,
} from './commandControlCharts';

const CommandControlPage = ({
  pageData,
  gameStatus,
  onDecisionSelect,
  displayButton = true,
  editMode = false,
  onEditDecision,
  onAddDecision,
  onEditScoreConfig,
}) => {
  const [selectedDecisions, setSelectedDecisions] = useState([]); // État pour suivre les décisions sélectionnées
  const [valideConstraint, setValidConstraint] = useState(true)
  // const [updatedScores, setUpdatedScores] = useState({}); // État pour la modification en directe de score

  const [listDisplayScore, setlistDisplayScore] = useState(() => {
    const DisplayScore = pageData.content.display_score_decisions || [];
    return DisplayScore.map(scoreObj => ({
      ...scoreObj,
      value: gameStatus.score_dic[scoreObj.score_id]?.value || 0
    }))
  }
  );

  // Le graphe : un ou deux scores ventilés par catégorie, sous la forme que la
  // page déclare (commandControlCharts.js). La ventilation se recalcule depuis
  // les mesures cochées à chaque rendu, plutôt que d'être tenue clic par clic.
  const chartDisplay = chartDisplayOf(pageData.content);
  const chartSeries = useMemo(
    () => chartSeriesOf(pageData.content, gameStatus.score_dic),
    [pageData.content, gameStatus.score_dic],
  );

  // Préparer les données à retourner
  // const prepareReturnData = () => {
  //   setUpdatedScores({
  //     score_in_doughnut: {
  //       metadata: {'standard' : standardWaterUsage, 'current' : waterUsageData},
  //     },
  //   });
  //   setPendingValidation(true); // Indique qu'on attend la mise à jour avant de valider
  // };

  const prepareReturnData = () => {
    return {
      page_id: pageData._id,
      page_type: pageData.type,
      page_general_consequence: pageData.page_general_consequence,
      round: pageData.round,
      content: {
        decision_list: pageData.content.list_decisions.map((decision) => ({
          ...decision,
          taken: selectedDecisions.some((selected) => selected._id === decision._id),
        })),
        updated_score : {}
      }
    };
  };

  // useEffect(() => {
  //   if (pendingValidation) {
  //     const preparedData = {
  //       page_id: pageData._id,
  //       page_type: pageData.type,
  //       page_general_consequence: pageData.page_general_consequence,
  //       content: {
  //         decision_list: pageData.content.list_decisions.map((decision) => ({
  //           ...decision,
  //           taken: selectedDecisions.some((selected) => selected._id === decision._id),
  //         })),
  //         updated_score : updatedScores
  //       }
  //     };
  //     console.log("Updated scores", updatedScores);
  //     onDecisionSelect(preparedData); // Envoie les données une fois `updatedScores` mis à jour
  //     setPendingValidation(false); // Réinitialise le flag
  //   }
  // }, [updatedScores]);

  // Gérer la sélection des décisions
  const handleDecisionClick = (decision) => {
    if (editMode) return; // en édition, le clic sur la carte ne sélectionne pas — seul le crayon agit
    if (!displayButton) return; // partie terminée : plus rien à valider, cocher une case ne serait qu'un leurre
    if (selectedDecisions.some((selected) => selected._id === decision._id)) {
      // Retire une décision déjà sélectionnée
      setSelectedDecisions(selectedDecisions.filter((selected) => selected._id !== decision._id));
  
      setlistDisplayScore((prevScores) => {
        let newScores = prevScores.map((scoreObj) => {
          const impact_dic = decision.score[scoreObj.score_id] ?? { impact: 0 };
          return {
            ...scoreObj,
            value: (scoreObj.value || 0) - impact_dic.impact
          };
        });
  
        // Vérification des seuils après mise à jour
        const valid = newScores.every(score => 
          score.threshold === undefined || score.value >= score.threshold
        );
        setValidConstraint(valid);
  
        return newScores;
      });
    } else {
      // Ajoute une décision
      setSelectedDecisions([...selectedDecisions, decision]);
  
      setlistDisplayScore((prevScores) => {
        let newScores = prevScores.map((scoreObj) => {
          const impact_dic = decision.score[scoreObj.score_id] ?? { impact: 0 };
          return {
            ...scoreObj,
            value: (scoreObj.value || 0) + impact_dic.impact
          };
        });
  
        // Vérification des seuils après mise à jour
        const valid = newScores.every(score => 
          score.threshold === undefined || score.value >= score.threshold
        );
        setValidConstraint(valid);
  
        return newScores;
      });
    }
  };

  // Valider et transmettre les données
  const handleValidation = () => {
    if (selectedDecisions.length > 0) {
      const preparedData = prepareReturnData();
      onDecisionSelect(preparedData); // Envoie les données transformées au parent
    } else {
      alert("Veuillez sélectionner au moins une option avant de valider.");
    }
  };

  const percentOf = (series) => savingsPercent(series, savingsWith(series, selectedDecisions));

  const renderChart = (series) => {
    const data = breakdownWith(series, selectedDecisions);
    if (chartDisplay === 'bars') return <BreakdownBars series={series} data={data} />;
    if (chartDisplay === 'half_doughnut') return <HalfDoughnutChart series={series} data={data} />;
    return (
      <WaterUsageChart
        data={data}
        unit={series.unit}
        totalInitConsumption={series.standardTotal}
        colorOffset={series.colorOffset}
      />
    );
  };

  return (
    <div className="page-general-container">
      <div className='page-general-background-color'></div>

      <div className='page-general-background-hero' style={{ backgroundImage: `url(/img/bgrille-m.svg)` }}>
          <div className='page-general-background-image' style={{ backgroundImage: `url(${pageData.content.background_image_url})` }}></div>
      </div>
      <div className='page-main-content'>
        <GameHeader gameStatus={gameStatus} listScore={listDisplayScore} pageName={pageData.title}></GameHeader>
        <div className='command-control-two-columns'>
          <div className='command-control-graph-and-legend'>
            {editMode && (
              <button
                className="builder-edit-fab builder-edit-fab-scores"
                onClick={onEditScoreConfig}
                title="Configurer le score du donut et les scores affichés"
              >⚙️ Configurer le graphe</button>
            )}
            {chartSeries.length === 0 ? (
              // Aucun score dessinable : le validateur l'a signalé à l'auteur.
              // En salle, la liste des mesures reste jouable sans son graphe.
              editMode && <div className='builder-warning-inline'>Aucun score à dessiner : choisissez-en un dans l'onglet Scores.</div>
            ) : chartSeries.length === 1 ? (
              // UN SEUL SCORE : l'écran d'avant, à l'identique pour l'anneau —
              // les pages écrites jusqu'ici n'ont pas à changer d'allure.
              <>
                <div className={`command-control-graph-item command-control-graph-item--${chartDisplay}`}>
                  {renderChart(chartSeries[0])}
                </div>
                <div className='command-control-legend'>
                  <img
                      src="/icon/water.png"
                      className="explanation-page-indicator-icon-image"
                      alt="Calendar"
                    />
                  <h2>Économies d'eau: {percentOf(chartSeries[0])}%</h2>
                </div>
              </>
            ) : (
              // DEUX SCORES : chacun son titre et sa réduction, dans la même
              // carte — ils se lisent ensemble, une mesure à la fois sur les deux.
              <div className={`command-control-graph-item command-control-graph-item--pair command-control-graph-item--${chartDisplay}`}>
                {chartSeries.map((series) => (
                  <div key={series.scoreId} className='command-control-chart-block'>
                    <div className='command-control-chart-head'>
                      {series.icon && <img src={series.icon} className='command-control-chart-icon' alt='' />}
                      <h3 className='command-control-chart-title'>{series.name}</h3>
                      <span className='command-control-chart-savings'>−{percentOf(series)} %</span>
                    </div>
                    {renderChart(series)}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="command-control-list">
            {pageData.content.list_decisions.map((decision, index) => (
              <div
                key={index}
                className={`command-control-decision ${
                  selectedDecisions.some((selected) => selected._id === decision._id)
                    ? 'command-control-decision-selected'
                    : ''
                } ${editMode ? 'builder-editable-card' : ''}`}
                onClick={() => handleDecisionClick(decision)}
              >
                {editMode && (
                  <button
                    className="builder-edit-fab builder-edit-fab-card"
                    onClick={(e) => { e.stopPropagation(); onEditDecision(decision._id); }}
                    title="Modifier cette décision"
                  >✏️</button>
                )}
                <div className="command-control-decision-details">
                  <h2>{decision.title}</h2>
                  {/* <p>{decision.description}</p> */}
                </div>
                <div className='command-control-decision-score-list-container'>
                  {pageData.content.display_score_decisions &&
                    pageData.content.display_score_decisions.map((score_info) => {
                      // Retrieve the correct impact value from the decision's score
                      const impact_dic = decision.score[score_info.score_id] ?? { impact: 0 };
                      const score = impact_dic.impact * -1; // Negate the impact for display

                      return (
                        <div className='command-control-decision-score-container' key={score_info.score_id}>
                          {score_info.display_type === 'multiple_icon' ? (
                            <div className="command-control-icons">
                              {Array.from({ length: Math.abs(score) }).map((_, index) => (
                                <img key={index} className='mosaic-decision-score-icon' src={score_info.icon_url} alt="icon" />
                              ))}
                            </div>
                          ) : (
                            <>
                              <div>{score}</div>
                              <img className='mosaic-decision-score-icon' src={score_info.icon_url} alt="icon" />
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>


              </div>
            ))}
            {editMode && (
              <div className="command-control-decision builder-add-card" onClick={onAddDecision} title="Ajouter une décision">
                <span className="builder-add-card-plus">+</span>
                <span>Ajouter une décision</span>
              </div>
            )}
          </div>
        </div>

        {!editMode && displayButton && (
          <button
            className="orange-button"
            onClick={handleValidation}
            disabled={!valideConstraint}
          >
            Valider
          </button>
        )}
      </div>
    </div>
  );
};

export default CommandControlPage;
